import {
  SERVER_IDENTIFIER_ICON,
  SERVER_IDENTIFIER_LABEL,
} from "@constants/server-constants";
import { Checkmark3Icon, Button, FontWeights } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props {
  /** Server identifier */
  serverIdentifier: FaroCoreApiTypes.ServerIdentifier;

  /** Callback triggered when the user clicks the button */
  onClick(serverIdentifier: FaroCoreApiTypes.ServerIdentifier): void;

  /** The currently selected server identifier */
  selectedServerIdentifier: FaroCoreApiTypes.ServerIdentifier;

  /** Whether the button is disabled */
  isDisabled?: boolean;
}

/** Renders a button to select a server region */
export function TrialServerButton({
  serverIdentifier,
  onClick,
  selectedServerIdentifier,
  isDisabled,
}: Props): JSX.Element {
  return (
    <Button
      variant="ghost"
      onClick={() => onClick(serverIdentifier)}
      disableFocusRipple={true}
      disableRipple={true}
      disableElevation={true}
      disableTouchRipple={true}
      disabled={isDisabled}
      sx={{
        // The `ghost` variant tints the text with the primary color and hides the border
        // while hovered, pressed and disabled. Those states are listed explicitly because they
        // are more specific than the idle one, so they would win over it otherwise.
        "&, &:hover, &:active, &.Mui-disabled": {
          border: "1px solid",
          borderColor: "input.enabledBorder",
          color:
            selectedServerIdentifier === serverIdentifier
              ? "primary.main"
              : "text.primary",
        },
        borderRadius: "0px",
        minHeight: "39px",
        flexGrow: 1,
        // Set as longhands so they win over the padding of the design system size variant.
        paddingTop: "6px",
        paddingBottom: "6px",
        paddingLeft: "10px",
        paddingRight: "8px",
        textAlign: "left",
        marginBottom: "24px",
        textTransform: "capitalize",
        fontSize: "14px",
        fontWeight: FontWeights.SemiBold,
        "&:first-of-type": {
          // Adjust first element border style
          borderRadius: "4px 0 0 4px",
        },
        "&:last-of-type": {
          // Adjust last element border style
          borderRadius: "0 4px 4px 0",
          borderLeft: "none",
        },
        // Push the label to the left and the checkmark to the right.
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {SERVER_IDENTIFIER_ICON[serverIdentifier]}
        {SERVER_IDENTIFIER_LABEL[serverIdentifier]}
      </Box>
      <Checkmark3Icon
        sx={{
          color: "primary.main",
          visibility:
            selectedServerIdentifier === serverIdentifier
              ? "visible"
              : "hidden",
        }}
      />
    </Button>
  );
}
